import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useGetGymsQuery, useGetSessionsQuery } from "../../../services";
import {
  useAssignSessionScheduleMutation,
  useGetSessionSchedulesByGymQuery,
  useGetSessionSchedulesQuery,
  useDeleteSessionScheduleMutation,
  useEditGymMutation,
} from "../../../services";
import { toast } from "../../../core/toast";
import { selectOrganisationId } from "../../../redux/organisationReducer";
import { GymRepeatSchedulePeriodType } from "../../../types/gym/gym.types";

const weekDays = ["S", "M", "T", "W", "T", "F", "S"];
function getInitialCalendar(view: string) {
  if (view === "week") {
    return Array(7)
      .fill([])
      .map(() => []);
  } else {
    // 6 rows x 7 cols
    return Array(42)
      .fill([])
      .map(() => []);
  }
}

// Build a 42-cell grid including previous/next month dates
function getMonthGrid(year: number, month: number) {
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const grid: { day: number; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const dayNum = i - startDay + 1;
    if (dayNum <= 0) grid.push({ day: prevMonthDays + dayNum, inMonth: false });
    else if (dayNum > daysInMonth)
      grid.push({ day: dayNum - daysInMonth, inMonth: false });
    else grid.push({ day: dayNum, inMonth: true });
  }
  return grid;
}

// ADD: helpers to handle dates in local timezone (avoid UTC ISO shift)
const toLocalYMD = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
const parseDateFromApi = (value: any): Date => {
  if (!value) return new Date(NaN);
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y, m - 1, d); // local date (no timezone shift)
  }
  return new Date(value);
};

// WEEK VIEW CELL - theme-aware
// Types for session items and calendar cells
type SessionItem = {
  id: string;
  scheduleId?: string;
  name: string;
  type: string;
};

type CalendarCellWeekProps = {
  items: SessionItem[];
  onDrop: (item: SessionItem, cellIdx: number) => void;
  cellIdx: number;
  getDateForCell: (cellIdx: number) => Date;
  onRemove?: (session: SessionItem, cellIdx: number) => void;
};

const CalendarCellWeek: React.FC<CalendarCellWeekProps> = ({
  items,
  onDrop,
  cellIdx,
  getDateForCell,
  onRemove,
}) => {
  const { theme } = useTheme();
  const cellBg = theme === "dark" ? "#1a1a1a" : "#ffffff";
  const normalBorder = theme === "dark" ? "#817d7dff" : "#9CA3AF";
  const normalText = theme === "dark" ? "#9CA3AF" : "#4B5563";
  const chipBg = theme === "dark" ? "#2a2a2a" : "#F3F4F6";

  // Date check removed

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "SIDEBAR_ITEM",
    drop: (item) => onDrop(item as SessionItem, cellIdx),
    canDrop: (item) => {
      return true;
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  });

  const dropZoneStyle = {
    minHeight: "300px",
    height: "auto",
    background: cellBg,
    opacity: 1,
    cursor: "auto",
  };

  if (isOver && !canDrop) {
    dropZoneStyle.background = "#ef4444"; // Red background for invalid drop
  } else if (isOver && canDrop) {
    dropZoneStyle.background = "rgba(182, 203, 36, 0.2)"; // Green background for valid drop
  }

  return (
    <div ref={drop} className="flex flex-col gap-1.5 p-2" style={dropZoneStyle}>
      {items.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <span
            className="text-xs font-medium"
            style={{ color: theme === "dark" ? "#6B7280" : "#9CA3AF" }}
          >
            No sessions scheduled
          </span>
        </div>
      ) : (
        items.map((session: SessionItem, sidx: number) => {
          const isPremium = session.type === "Premium";
          const borderColor = isPremium ? "#B6CB24" : normalBorder;
          const textColor = isPremium ? "#B6CB24" : normalText;

          return (
            <div
              key={sidx}
              className="flex items-center justify-between gap-1.5 px-2.5 py-1 rounded-full font-medium text-xs"
              style={{
                backgroundColor: chipBg,
                border: `1px solid ${borderColor}`,
                color: textColor,
              }}
            >
              <span className="flex items-center gap-1.5 relative group">
                <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                  <circle
                    cx="9"
                    cy="9"
                    r="7"
                    stroke={borderColor}
                    strokeWidth="1.5"
                    fill={
                      isPremium ? "rgba(182, 203, 36, 0.15)" : "transparent"
                    }
                  />
                </svg>
                <span className="font-semibold cursor-default">
                  {session.name && session.name.length > 6
                    ? `${session.name.substring(0, 6)}...`
                    : session.name}
                </span>
                {session.name && session.name.length > 6 && (
                  <div
                    className={`absolute left-0 top-full mt-1.5 z-50 px-2.5 py-1.5 rounded-md text-xs font-medium shadow-lg whitespace-nowrap pointer-events-none transition-all duration-200 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 ${
                      theme === "dark"
                        ? "bg-[#2A2A2A] border border-[#3A3A3A] text-gray-200 shadow-black/40"
                        : "bg-white border border-gray-200 text-gray-800"
                    }`}
                  >
                    {session.name}
                  </div>
                )}
              </span>
              <button
                type="button"
                aria-label="Remove schedule"
                className={`h-5 w-5 flex items-center justify-center rounded-full border ${
                  theme === "dark"
                    ? "border-gray-500 text-gray-300"
                    : "border-gray-400 text-gray-600"
                } hover:bg-red-500 hover:text-white hover:border-red-500 transition`}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove?.(session, cellIdx);
                }}
              >
                ×
              </button>
            </div>
          );
        })
      )}
    </div>
  );
};

// MONTH VIEW CELL - theme-aware
// Types for month cell props
type CalendarCellMonthProps = {
  items: SessionItem[];
  onDrop: (item: SessionItem, cellIdx: number) => void;
  cellIdx: number;
  date: number;
  inMonth: boolean;
  isToday: boolean;
  getDateForCell: (cellIdx: number) => Date;
};

const CalendarCellMonth: React.FC<CalendarCellMonthProps> = ({
  items,
  onDrop,
  cellIdx,
  date,
  inMonth,
  isToday,
  getDateForCell,
}) => {
  const { theme } = useTheme();
  const cellBg = theme === "dark" ? "#1a1a1a" : "#ffffff";
  const inMonthColor = theme === "dark" ? "#D1D5DB" : "#374151";
  const outMonthColor = theme === "dark" ? "#6B7280" : "#9CA3AF";
  const badgeBg = theme === "dark" ? "#2a2a2a" : "#F3F4F6";
  const normalBadgeBorder = theme === "dark" ? "#6B6B6B" : "#D1D5DB";
  const normalBadgeText = theme === "dark" ? "#9CA3AF" : "#4B5563";

  // Date check removed

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "SIDEBAR_ITEM",
    drop: (item) => onDrop(item as SessionItem, cellIdx),
    canDrop: (item) => {
      return true;
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  });

  const premiumCount = items.filter(
    (i: SessionItem) => i.type === "Premium",
  ).length;
  const normalCount = items.filter(
    (i: SessionItem) => i.type === "Normal",
  ).length;

  const dropZoneStyle = {
    minHeight: "80px",
    height: "auto",
    background: cellBg,
    opacity: 1,
  };

  if (isOver && !canDrop) {
    dropZoneStyle.background = "#ef4444"; // Red background for invalid drop
  } else if (isOver && canDrop) {
    dropZoneStyle.background = "rgba(182, 203, 36, 0.2)"; // Green background for valid drop
  }

  return (
    <div
      ref={drop}
      className="flex flex-col gap-2 p-2 relative"
      style={dropZoneStyle}
    >
      <div
        className="absolute left-2 top-1 text-xs font-medium"
        style={{ color: inMonth ? inMonthColor : outMonthColor }}
      >
        {date}
      </div>

      {/* Count badges */}
      {(premiumCount > 0 || normalCount > 0) && (
        <div className="flex gap-2 mt-5">
          {premiumCount > 0 && (
            <div
              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
              style={{
                backgroundColor: badgeBg,
                border: "1px solid #B6CB24",
                color: "#B6CB24",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 18 18" fill="none">
                <circle
                  cx="9"
                  cy="9"
                  r="7"
                  stroke="#B6CB24"
                  strokeWidth="1.5"
                  fill="rgba(182,203,36,0.15)"
                />
              </svg>
              {premiumCount}
            </div>
          )}
          {normalCount > 0 && (
            <div
              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
              style={{
                backgroundColor: badgeBg,
                border: `1px solid ${normalBadgeBorder}`,
                color: normalBadgeText,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 18 18" fill="none">
                <circle
                  cx="9"
                  cy="9"
                  r="7"
                  stroke={normalBadgeBorder}
                  strokeWidth="1.5"
                />
              </svg>
              {normalCount}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Sidebar item - theme-aware
// Sidebar item props
type SidebarItemProps = {
  item: SessionItem;
};

const SidebarItem: React.FC<SidebarItemProps> = ({ item }) => {
  const { theme } = useTheme();
  const [{ isDragging }, drag] = useDrag({
    type: "SIDEBAR_ITEM",
    item,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const boxCls =
    theme === "dark"
      ? "bg-[#232323] text-gray-100 border-[#232323]"
      : "bg-white text-gray-900 border-gray-300";

  return (
    <div
      ref={drag}
      className={`flex items-center justify-between px-3 py-2 rounded-lg mb-2 border cursor-move ${boxCls}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className="flex items-center gap-2 relative group">
        <span className="font-semibold text-sm cursor-default">
          {item.name && item.name.length > 18
            ? `${item.name.substring(0, 18)}...`
            : item.name}
        </span>
        {item.name && item.name.length > 6 && (
          <div
            className={`absolute left-0 top-full mt-1.5 z-50 px-2.5 py-1.5 rounded-md text-xs font-medium shadow-lg whitespace-nowrap pointer-events-none transition-all duration-200 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 ${
              theme === "dark"
                ? "bg-[#2A2A2A] border border-[#3A3A3A] text-gray-200 shadow-black/40"
                : "bg-white border border-gray-200 text-gray-800"
            }`}
          >
            {item.name}
          </div>
        )}
      </div>
      <span
        className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
          item.type === "Premium"
            ? "border-[#B6CB24] text-[#B6CB24]"
            : theme === "dark"
              ? "border-gray-400 text-gray-400"
              : "border-gray-300 text-gray-600"
        } ${theme === "dark" ? "bg-[#232323]" : "bg-white"}`}
      >
        {item.type}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <circle
            cx="6"
            cy="6"
            r="5"
            stroke={
              item.type === "Premium"
                ? "#B6CB24"
                : theme === "dark"
                  ? "#A0A0A0"
                  : "#9CA3AF"
            }
            strokeWidth="2"
          />
        </svg>
      </span>
    </div>
  );
};

const Schedule: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const gymIdFromUrl = searchParams.get("gymId") || "";
  const [selectedGymId, setSelectedGymId] = useState<string>(gymIdFromUrl);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Sync state with URL when component mounts or searchParams change
  useEffect(() => {
    if (gymIdFromUrl && gymIdFromUrl !== selectedGymId) {
      setSelectedGymId(gymIdFromUrl);
    }
  }, [gymIdFromUrl, selectedGymId]);
  const organisationId = useAppSelector(selectOrganisationId);

  const {
    data: gymsData,
    isLoading: isGymsLoading,
    isError: isGymsError,
  } = useGetGymsQuery(organisationId);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sessionsList, setSessionsList] = useState<any[]>([]);

  // Reset list when search changes
  useEffect(() => {
    setPage(1);
    setSessionsList([]);
  }, [debouncedSearch]);

  const {
    data: sessionsData,
    isFetching: isSessionsLoading,
    isError: isSessionsError,
  } = useGetSessionsQuery({ name: debouncedSearch, page, limit: 15 });

  useEffect(() => {
    if (sessionsData) {
      const src: any = sessionsData;
      let newItems: any[] = [];
      if (Array.isArray(src)) newItems = src;
      else if (Array.isArray(src?.data)) newItems = src.data;
      else if (Array.isArray(src?.items)) newItems = src.items;
      else if (Array.isArray(src?.results)) newItems = src.results;

      if (newItems.length < 15) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      setSessionsList((prev) => {
        if (page === 1) return newItems;
        const prevIds = new Set(
          prev.map((p: any) => p._id || p.id || p.sessionId),
        );
        const filtered = newItems.filter(
          (n: any) => !prevIds.has(n._id || n.id || n.sessionId),
        );
        return [...prev, ...filtered];
      });
    }
  }, [sessionsData, page]);

  // Fetch all session schedules (for initial load)
  const {
    data: allSessionSchedulesData,
    isLoading: isAllSessionSchedulesLoading,
    isError: isAllSessionSchedulesError,
  } = useGetSessionSchedulesQuery(organisationId);

  // Fetch session schedules by selected gym
  const {
    data: gymSessionSchedulesData,
    isLoading: isGymSessionSchedulesLoading,
    isError: isGymSessionSchedulesError,
  } = useGetSessionSchedulesByGymQuery(selectedGymId, {
    skip: !selectedGymId,
  });

  // Add create schedule mutation
  const [assignSessionSchedule] = useAssignSessionScheduleMutation();
  const [deleteSessionSchedule] = useDeleteSessionScheduleMutation();
  const [editGym] = useEditGymMutation();

  const [repeatOption, setRepeatOption] = useState<
    GymRepeatSchedulePeriodType | "No Repeat"
  >("No Repeat");

  const gymsList = React.useMemo(() => {
    const src: any = gymsData;
    if (!src) return [];
    if (Array.isArray(src)) return src;
    if (Array.isArray(src?.data)) return src.data;
    if (Array.isArray(src?.items)) return src.items;
    if (Array.isArray(src?.results)) return src.results;
    return [];
  }, [gymsData]);

  const sessionSchedulesList = React.useMemo(() => {
    const src: any = selectedGymId
      ? gymSessionSchedulesData
      : allSessionSchedulesData;
    if (!src) return [];
    if (Array.isArray(src)) return src;
    if (Array.isArray(src?.data)) return src.data;
    if (Array.isArray(src?.items)) return src.items;
    if (Array.isArray(src?.results)) return src.results;
    return [];
  }, [selectedGymId, gymSessionSchedulesData, allSessionSchedulesData]);

  // Get current loading and error states
  const isSessionSchedulesLoading = selectedGymId
    ? isGymSessionSchedulesLoading
    : isAllSessionSchedulesLoading;
  const isSessionSchedulesError = selectedGymId
    ? isGymSessionSchedulesError
    : isAllSessionSchedulesError;

  const getGymId = (g: any) =>
    g?.id ?? g?._id ?? g?.gymId ?? g?.uuid ?? `${g?.value ?? ""}`;
  const getGymLabel = (g: any) =>
    g?.name ?? g?.gymName ?? g?.title ?? g?.label ?? `Gym ${getGymId(g)}`;

  const [calendarView, setCalendarView] = useState<"week" | "month">("week");
  const [calendarData, setCalendarData] = useState<SessionItem[][]>(
    getInitialCalendar("week"),
  );

  // State for current month/year
  const [currentDate, setCurrentDate] = useState(new Date());

  // State for Repeater Modal
  const [isRepeaterModalOpen, setIsRepeaterModalOpen] = useState(false);

  // Sync repeater option when modal opens
  useEffect(() => {
    if (isRepeaterModalOpen && selectedGymId) {
      const selectedGym = gymsList.find(
        (g: any) => getGymId(g) === selectedGymId,
      );
      if (selectedGym) {
        if (selectedGym.autoRepeatSchedule && selectedGym.repeatPeriodType) {
          setRepeatOption(selectedGym.repeatPeriodType as any);
        } else {
          setRepeatOption("No Repeat");
        }
      }
    }
  }, [isRepeaterModalOpen, selectedGymId, gymsList]);

  // Keep a 42-cell grid for the current month
  const [monthGrid, setMonthGrid] = useState(
    getMonthGrid(currentDate.getFullYear(), currentDate.getMonth()),
  );

  // Recompute grid and reset calendar data when month/view changes
  useEffect(() => {
    if (calendarView === "month") {
      setMonthGrid(
        getMonthGrid(currentDate.getFullYear(), currentDate.getMonth()),
      );
      setCalendarData(getInitialCalendar("month"));
    } else {
      setCalendarData(getInitialCalendar("week"));
    }
  }, [currentDate, calendarView]);

  // Helper for month name
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthLabel = `${
    monthNames[currentDate.getMonth()]
  } ${currentDate.getFullYear()}`;

  // Handlers for prev/next month
  const handlePrevMonth = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  };
  const handleNextMonth = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  };
  const handleToday = () => {
    setCurrentDate(new Date());
  };

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  // Helper function to get date for dropped cell
  const getDateForCell = (cellIdx: number) => {
    if (calendarView === "week") {
      // Use the week that contains currentDate
      const anchor = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
      );
      anchor.setHours(0, 0, 0, 0);
      const startOfWeek = new Date(anchor);
      startOfWeek.setDate(anchor.getDate() - anchor.getDay()); // Sunday start
      const cellDate = new Date(startOfWeek);
      cellDate.setDate(startOfWeek.getDate() + cellIdx);
      return cellDate;
    } else {
      // Month view
      const cellData = monthGrid[cellIdx];
      if (!cellData) return new Date();

      let targetDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        cellData.day,
      );

      // If it's not in current month, adjust the date
      if (!cellData.inMonth) {
        if (cellData.day > 15) {
          // Previous month
          targetDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() - 1,
            cellData.day,
          );
        } else {
          // Next month
          targetDate = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            cellData.day,
          );
        }
      }

      return targetDate;
    }
  };

  // Handle drop from sidebar to calendar cell
  const handleDrop = async (item: SessionItem, cellIdx: number) => {
    // Check if gym is selected
    if (!selectedGymId) {
      toast.error("Please select a gym first before scheduling sessions.");
      return;
    }

    const scheduleDate = getDateForCell(cellIdx);
    // Past date check removed

    // Use local date (no UTC) for comparisons and payload
    const dateString = toLocalYMD(scheduleDate);

    // Check if this specific session already exists for this gym on this date
    const duplicateSession = sessionSchedulesList.find((schedule: any) => {
      const scheduleGymId =
        schedule.gym?.id || schedule.gym?._id || schedule.gymId || schedule.gym;
      const scheduleSessionId =
        schedule.session?.id ||
        schedule.session?._id ||
        schedule.sessionId ||
        schedule.session;

      // Parse API date safely (preserve local date if it's YYYY-MM-DD)
      const schedDateStr = toLocalYMD(
        parseDateFromApi(
          schedule.date || schedule.scheduledDate || schedule.schedule_date,
        ),
      );

      return (
        String(scheduleGymId) === String(selectedGymId) &&
        String(scheduleSessionId) === String(item.id) &&
        schedDateStr === dateString
      );
    });

    if (duplicateSession) {
      toast.error(
        `Session "${
          item.name
        }" is already scheduled for this gym on ${scheduleDate.toLocaleDateString()}.`,
      );
      return;
    }

    try {
      // Send assignments array to match API
      const scheduleData = {
        session: item.id,
        gym: selectedGymId,
        date: dateString,
      };
      await assignSessionSchedule(scheduleData).unwrap();

      // Optimistic local update; API invalidation will sync
      setCalendarData((prev) =>
        prev.map((cell, idx) => (idx === cellIdx ? [...cell, item] : cell)),
      );
    } catch (error) {
      console.error("Failed to create schedule:", error);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState<{
    cellIdx: number;
    sessionItem: any;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Replace previous delete logic with modal trigger
  const handleRemove = async (sessionItem: any, cellIdx: number) => {
    setDeleteTarget({ cellIdx, sessionItem });
  };

  const closeDelete = () => {
    if (isDeleting) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { cellIdx, sessionItem } = deleteTarget;
    setIsDeleting(true);
    try {
      // original delete logic moved here
      let scheduleId = sessionItem.scheduleId;
      if (!scheduleId) {
        const cellDate = getDateForCell(cellIdx);
        const dateString = toLocalYMD(cellDate);
        const match = sessionSchedulesList.find((s: any) => {
          const sid =
            s.session?._id || s.sessionId || s.session?.id || s.session;
          const gid = s.gym?._id || s.gym?.id || s.gymId || s.gym;
          const d = toLocalYMD(
            parseDateFromApi(s.date || s.scheduledDate || s.schedule_date),
          );
          return (
            String(sid) === String(sessionItem.id) &&
            String(gid) === String(selectedGymId || s.gym) &&
            d === dateString
          );
        });
        scheduleId = match?._id || match?.id;
      }

      if (!scheduleId) {
        toast.error("Cannot find schedule to delete");
        setIsDeleting(false);
        return;
      }

      await deleteSessionSchedule(scheduleId).unwrap();

      setCalendarData((prev) =>
        prev.map((cell, idx) =>
          idx === cellIdx ? cell.filter((it: any) => it !== sessionItem) : cell,
        ),
      );
      setDeleteTarget(null);
    } catch {
      // errors handled upstream
    } finally {
      setIsDeleting(false);
    }
  };

  // Load scheduled sessions into calendar when data changes
  useEffect(() => {
    if (sessionSchedulesList.length > 0) {
      const newCalendarData: SessionItem[][] = getInitialCalendar(calendarView);

      sessionSchedulesList.forEach((schedule: any) => {
        const scheduleDate = parseDateFromApi(
          schedule.date || schedule.scheduledDate || schedule.schedule_date,
        );
        const cellIdx = getCellIndexForDate(scheduleDate);
        if (cellIdx !== -1) {
          const sessionItem: SessionItem = {
            // keep session id to detect duplicates
            id:
              schedule.session?._id ||
              schedule.sessionId ||
              schedule.session?.id ||
              schedule.session,
            scheduleId: schedule._id || schedule.id, // for delete
            name:
              schedule.session?.name ||
              schedule.session?.sessionCode ||
              schedule.session?.code ||
              schedule.sessionCode ||
              schedule.code ||
              `Session ${schedule.id}`,
            type:
              schedule.session?.type ||
              schedule.session?.sessionType ||
              schedule.type ||
              schedule.sessionType ||
              "Normal",
          };
          newCalendarData[cellIdx] = [...newCalendarData[cellIdx], sessionItem];
        }
      });

      setCalendarData(newCalendarData);
    } else {
      setCalendarData(getInitialCalendar(calendarView));
    }
  }, [sessionSchedulesList, calendarView, currentDate]);

  // Helper function to get cell index for a date
  const getCellIndexForDate = (date: Date): number => {
    if (calendarView === "week") {
      // Map within the week that contains currentDate
      const anchor = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
      );
      anchor.setHours(0, 0, 0, 0);
      const startOfWeek = new Date(anchor);
      startOfWeek.setDate(anchor.getDate() - anchor.getDay()); // Sunday start
      const daysDiff = Math.floor(
        (date.getTime() - startOfWeek.getTime()) / (1000 * 60 * 60 * 24),
      );
      return daysDiff >= 0 && daysDiff < 7 ? daysDiff : -1;
    } else {
      // Month view
      for (let i = 0; i < monthGrid.length; i++) {
        const cellData = monthGrid[i];
        let cellDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          cellData.day,
        );

        // Adjust for previous/next month cells
        if (!cellData.inMonth) {
          if (cellData.day > 15) {
            cellDate = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth() - 1,
              cellData.day,
            );
          } else {
            cellDate = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth() + 1,
              cellData.day,
            );
          }
        }

        if (cellDate.toDateString() === date.toDateString()) {
          return i;
        }
      }
      return -1;
    }
  };

  // Handle gym selection change
  const handleGymChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const gymId = e.target.value;
    setSelectedGymId(gymId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (gymId) {
        next.set("gymId", gymId);
      } else {
        next.delete("gymId");
      }
      return next;
    });
  };

  // Add: filter change handler (fixes undefined error)
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextView: "week" | "month" =
      e.target.value === "Filter by Month" ? "month" : "week";
    setCalendarView(nextView);
    setCalendarData(getInitialCalendar(nextView));
    // monthGrid will recompute via useEffect when calendarView changes
  };

  // Render calendar grid (use theme-aware borders and header bg)
  const renderCalendar = () => {
    // theme-aware border color for grids
    const borderColor = theme === "dark" ? "#2a2a2a" : "#e5e7eb";
    const headerBg = theme === "dark" ? "#232323" : "#F3F4F1";
    const headerTextCls = theme === "dark" ? "text-gray-300" : "text-gray-700";

    if (calendarView === "week") {
      // Build the week that contains currentDate
      const anchor = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
      );
      anchor.setHours(0, 0, 0, 0);
      const startOfWeek = new Date(anchor);
      startOfWeek.setDate(anchor.getDate() - anchor.getDay()); // Sunday start

      const weekDates = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        return date;
      });

      return (
        <div
          className="grid  grid-cols-7 gap-0 rounded-lg overflow-hidden"
          style={{ border: `1px solid ${borderColor}` }}
        >
          {/* Header row with dates */}
          <div className="col-span-7 grid grid-cols-7">
            {weekDays.map((day, idx) => {
              const date = weekDates[idx];
              const isToday = date.toDateString() === new Date().toDateString();
              return (
                <div
                  key={day}
                  className={`text-center font-semibold py-2 text-sm ${headerTextCls}`}
                  style={{
                    backgroundColor: headerBg, // no color highlight for today
                    borderRight: idx < 6 ? `1px solid ${borderColor}` : "none",
                    borderBottom: `1px solid ${borderColor}`,
                    ...(isToday
                      ? {
                          // outline current weekday in header (top and sides)
                          borderLeft: `2px solid #B6CB24`,
                          borderRight: `2px solid #B6CB24`,
                          borderTop: `2px solid #B6CB24`,
                        }
                      : {}),
                  }}
                >
                  <div>{day}</div>
                  {/* removed date number */}
                  {/* <div className="text-xs font-normal">{date.getDate()}</div> */}
                </div>
              );
            })}
          </div>
          {/* Calendar cells */}
          {weekDays.map((day, idx) => {
            const date = weekDates[idx];
            const isToday = date.toDateString() === new Date().toDateString();
            return (
              <div
                key={day}
                className="flex flex-col"
                style={{
                  borderRight: idx < 6 ? `1px solid ${borderColor}` : "none",
                  ...(isToday
                    ? {
                        // outline current weekday column (bottom and sides)
                        borderLeft: `2px solid #B6CB24`,
                        borderRight: `2px solid #B6CB24`,
                        borderBottom: `2px solid #B6CB24`,
                      }
                    : {}),
                }}
              >
                <CalendarCellWeek
                  items={calendarData[idx] || []}
                  onDrop={handleDrop}
                  cellIdx={idx}
                  getDateForCell={getDateForCell}
                  onRemove={handleRemove}
                />
              </div>
            );
          })}
        </div>
      );
    } else {
      const today = new Date();
      return (
        <div
          className="rounded-lg overflow-hidden"
          style={{ border: `1px solid ${borderColor}` }}
        >
          {/* Header row */}
          <div className="grid grid-cols-7">
            {weekDays.map((day, idx) => (
              <div
                key={day}
                className={`text-center font-semibold py-2 text-sm ${headerTextCls}`}
                style={{
                  backgroundColor: headerBg,
                  borderRight: idx < 6 ? `1px solid ${borderColor}` : "none",
                  borderBottom: `1px solid ${borderColor}`,
                }}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Month cells */}
          <div className="grid grid-cols-7">
            {monthGrid.map((d, idx) => {
              const isToday =
                d.inMonth &&
                today.getDate() === d.day &&
                today.getMonth() === currentDate.getMonth() &&
                today.getFullYear() === currentDate.getFullYear();

              return (
                <div
                  key={idx}
                  className="flex flex-col"
                  style={{
                    borderRight:
                      (idx + 1) % 7 !== 0 ? `1px solid ${borderColor}` : "none",
                    borderBottom:
                      idx < 35 ? `1px solid ${borderColor}` : "none",
                    ...(isToday ? { border: "2px solid #B6CB24" } : {}),
                  }}
                >
                  <CalendarCellMonth
                    items={calendarData[idx] || []}
                    onDrop={handleDrop}
                    cellIdx={idx}
                    date={d.day}
                    inMonth={d.inMonth}
                    isToday={isToday}
                    getDateForCell={getDateForCell}
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        className={`pt-20 pb-8 min-h-screen ${
          theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
        }`}
        style={{
          marginLeft: sidebarWidth,
          width: `calc(100% - ${sidebarWidth}px)`,
        }}
      >
        {/* Header */}
        <div className="px-4 md:px-6 lg:px-8 mb-2">
          <h1
            className={`text-xl font-semibold ${
              theme === "dark" ? "text-gray-100" : "text-gray-900"
            }`}
          >
            Schedule Overview
          </h1>
          <div className="text-sm mt-1 flex items-center gap-2">
            <span className="text-gray-400">Dashboard</span>
            <span className="text-gray-600">&gt;</span>
            <span className="text-[#B6CB24] font-medium">All Schedules</span>
          </div>
        </div>

        {/* Main Layout */}
        <div className="flex gap-4 px-4 md:px-6 lg:px-8">
          {/* Left Sidebar */}
          <div
            className={`w-[310px] min-w-[290px] rounded-xl shadow-lg border p-4 flex flex-col ${
              theme === "dark"
                ? "bg-[#181818] border-[#232323]"
                : "bg-white border-gray-200"
            }`}
            style={{ height: "480px" }}
          >
            <div className="flex items-center justify-between mb-4">
              <span
                className={`font-medium text-sm ${
                  theme === "dark" ? "text-gray-100" : "text-gray-900"
                }`}
              >
                Choose from List
              </span>
              <button
                className="flex items-center gap-2 px-3 py-1 rounded-full border border-[#B6CB24] font-medium text-[#B6CB24] bg-[#232323] hover:bg-[#232323]/90 transition"
                style={{
                  background:
                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198,217,44,0.12) 0%, rgba(83,91,16,0.12) 100%)",
                }}
                onClick={() => navigate(adminRoutes.sessionCreate)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 2.00012C17.5228 2.00012 22 6.47727 22 12.0001C22 17.523 17.5228 22.0001 12 22.0001C6.47715 22.0001 2 17.523 2 12.0001M8.909 2.48699C7.9 2.8146 6.96135 3.29828 6.12153 3.90953M3.90943 6.12162C3.29806 6.9616 2.81432 7.90044 2.4867 8.90964"
                    stroke="#F3F3F6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 8.00012V16.0001M16 12.0001H8"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="font-medium text-xs">Create Session</span>
              </button>
            </div>

            {/* Search Bar Input */}
            <div className="mb-4 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className={`w-4 h-4 ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search session"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-9 pr-4 py-1.5 rounded-md text-sm ${
                  theme === "dark"
                    ? "bg-[#0B0B0B] border border-[#2C2C2C] text-gray-300 placeholder-gray-500"
                    : "bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                style={{ minWidth: "200px" }}
              />
            </div>
            <div
              className="flex-1 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-[#232323] scrollbar-track-[#181818]"
              onScroll={(e) => {
                const target = e.target as HTMLDivElement;
                if (
                  target.scrollHeight - target.scrollTop - target.clientHeight <
                    50 &&
                  !isSessionsLoading &&
                  hasMore
                ) {
                  setPage((p) => p + 1);
                }
              }}
            >
              {isSessionsLoading && page === 1 ? (
                <div
                  className={`text-center py-4 ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Loading sessions...
                </div>
              ) : isSessionsError && page === 1 ? (
                <div
                  className={`text-center py-4 ${
                    theme === "dark" ? "text-red-400" : "text-red-600"
                  }`}
                >
                  Failed to load sessions
                </div>
              ) : sessionsList.length === 0 ? (
                <div
                  className={`text-center py-4 ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  No sessions available
                </div>
              ) : (
                <>
                  {sessionsList.map((session: any, idx: number) => {
                    // Normalize session data
                    const sessionItem = {
                      id:
                        session?._id ||
                        session?.sessionId ||
                        session?.id ||
                        `session-${idx}`,
                      name: session?.name || session?.code || `S${idx + 1}`,
                      type: session?.type || session?.sessionType || "Normal",
                    };
                    return (
                      <SidebarItem key={sessionItem.id} item={sessionItem} />
                    );
                  })}
                  {isSessionsLoading && page > 1 && (
                    <div
                      className={`text-center py-2 text-sm font-medium ${theme === "dark" ? "text-[#B6CB24]" : "text-[#B6CB24]"}`}
                    >
                      Loading more...
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col gap-4">
            {/* Calendar Card */}
            <div
              className={`flex-1 flex flex-col gap-4 rounded-lg border p-3 ${
                theme === "dark"
                  ? "bg-[#181818] border-[#232323]"
                  : "bg-white border-gray-200"
              }`}
            >
              {/* Top controls */}
              <div
                className={`flex items-center justify-between p-2 rounded-lg mb-4 ${
                  theme === "dark"
                    ? "bg-[#181818]"
                    : "bg-white border border-gray-200"
                }`}
              >
                {/* 1. Select Gym */}
                <div className="w-[240px]">
                  <select
                    className={`w-full h-10 px-4 rounded-lg text-sm outline-none border ${
                      theme === "dark"
                        ? "bg-[#232323] border-[#383838] text-gray-300 focus:border-[#B6CB24]"
                        : "bg-gray-50 border-gray-300 text-gray-800"
                    }`}
                    value={selectedGymId}
                    onChange={handleGymChange}
                    disabled={isGymsLoading || isGymsError}
                  >
                    <option value="" disabled>
                      {isGymsLoading
                        ? "Loading gyms..."
                        : isGymsError
                          ? "Failed to load gyms"
                          : "Choose from list"}
                    </option>
                    {!isGymsLoading &&
                      !isGymsError &&
                      gymsList.map((g: any) => {
                        const id = getGymId(g);
                        const label = getGymLabel(g);
                        return (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        );
                      })}
                  </select>
                </div>

                {/* 2. Today Button */}
                <button
                  onClick={handleToday}
                  className="flex items-center gap-2 px-4 h-10 rounded-lg border border-[#C6D92C] text-white font-medium text-sm hover:opacity-90 transition"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M3 9V20C3 20.5523 3.44772 21 4 21H20C20.5523 21 21 20.5523 21 20V9M3 9V5C3 4.44772 3.44772 4 4 4H20C20.5523 4 21 4.44772 21 5V9M3 9H21M8 2V6M16 2V6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <rect
                      x="7"
                      y="12"
                      width="2"
                      height="2"
                      rx="0.5"
                      fill="currentColor"
                    />
                    <rect
                      x="7"
                      y="16"
                      width="2"
                      height="2"
                      rx="0.5"
                      fill="currentColor"
                    />
                    <rect
                      x="11"
                      y="12"
                      width="2"
                      height="2"
                      rx="0.5"
                      fill="currentColor"
                    />
                    <rect
                      x="11"
                      y="16"
                      width="2"
                      height="2"
                      rx="0.5"
                      fill="currentColor"
                    />
                    <rect
                      x="15"
                      y="12"
                      width="2"
                      height="2"
                      rx="0.5"
                      fill="currentColor"
                    />
                    <rect
                      x="15"
                      y="16"
                      width="2"
                      height="2"
                      rx="0.5"
                      fill="currentColor"
                    />
                  </svg>
                  Today
                </button>

                {/* 3. Month Nagivation */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={handlePrevMonth}
                    className={`p-1 hover:text-[#B6CB24] transition ${
                      theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <span className="text-[#B6CB24] font-bold text-lg min-w-[140px] text-center">
                    {monthLabel}
                  </span>
                  <button
                    onClick={handleNextMonth}
                    className={`p-1 hover:text-[#B6CB24] transition ${
                      theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                {/* 4. Set Schedule Repeater */}
                <button
                  onClick={() => setIsRepeaterModalOpen(true)}
                  disabled={!selectedGymId}
                  title={
                    !selectedGymId
                      ? "Please select a gym first to set schedule repeater"
                      : ""
                  }
                  className={`flex items-center gap-2 px-4 h-10 rounded-lg border text-sm font-medium transition ${
                    !selectedGymId
                      ? "opacity-50 cursor-not-allowed border-gray-500 text-gray-500"
                      : `hover:bg-[#B6CB24]/10 ${
                          theme === "dark"
                            ? "border-[#B6CB24] text-white"
                            : "border-[#B6CB24] text-gray-900"
                        }`
                  }`}
                  style={{
                    backgroundColor: !selectedGymId
                      ? theme === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.05)"
                      : theme === "dark"
                        ? "rgba(182, 203, 36, 0.20)"
                        : "rgba(182, 203, 36, 0.10)",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 8.77539C11.3676 8.77541 10.7493 8.96268 10.2232 9.31359C9.69703 9.6645 9.28656 10.1633 9.04352 10.7472C8.80048 11.3311 8.73576 11.9738 8.85752 12.5944C8.97927 13.215 9.28206 13.7857 9.72769 14.2345C10.1733 14.6832 10.7418 14.99 11.3616 15.1161C11.9813 15.2422 12.6245 15.182 13.2101 14.943C13.7956 14.7041 14.2973 14.2971 14.6519 13.7734C15.0065 13.2498 15.1981 12.6328 15.2025 12.0004C15.2055 11.5779 15.1249 11.1591 14.9652 10.7679C14.8056 10.3768 14.5702 10.0211 14.2725 9.72132C13.9749 9.42156 13.6208 9.18365 13.2308 9.0213C12.8408 8.85896 12.4225 8.77538 12 8.77539ZM12 13.5754C11.6831 13.5754 11.3734 13.4811 11.1102 13.3045C10.847 13.128 10.6423 12.8771 10.5221 12.5839C10.4019 12.2907 10.3716 11.9683 10.4351 11.6579C10.4986 11.3474 10.653 11.0628 10.8786 10.8403C11.1043 10.6178 11.391 10.4675 11.7024 10.4084C12.0137 10.3493 12.3356 10.3842 12.6271 10.5085C12.9186 10.6329 13.1665 10.8412 13.3393 11.1068C13.5121 11.3725 13.602 11.6835 13.5975 12.0004C13.5897 12.4195 13.4182 12.819 13.1196 13.1133C12.8211 13.4077 12.4193 13.5735 12 13.5754Z"
                      fill="white"
                    />
                    <path
                      d="M18.12 13.7326C18.3595 13.5081 18.5505 13.2369 18.681 12.9356C18.8116 12.6344 18.8789 12.3096 18.8789 11.9813C18.8789 11.653 18.8116 11.3282 18.681 11.027C18.5505 10.7258 18.3595 10.4546 18.12 10.2301L17.805 9.93757C17.7219 9.86012 17.6562 9.76592 17.6123 9.6612C17.5683 9.55648 17.5471 9.44361 17.55 9.33007V8.90257C17.5597 8.57391 17.5021 8.24672 17.3809 7.94107C17.2597 7.63543 17.0773 7.35775 16.845 7.12507C16.6136 6.89152 16.3359 6.70885 16.0298 6.58875C15.7238 6.46864 15.396 6.41375 15.0675 6.42757H14.64C14.5256 6.42836 14.4123 6.40619 14.3067 6.36239C14.201 6.31859 14.1052 6.25405 14.025 6.17257L13.7325 5.85757C13.267 5.39226 12.6357 5.13086 11.9775 5.13086C11.3193 5.13086 10.688 5.39226 10.2225 5.85757L9.92999 6.17257C9.85196 6.2549 9.75765 6.32008 9.65307 6.36398C9.54848 6.40788 9.4359 6.42954 9.32249 6.42757H8.89499C8.56588 6.41425 8.23764 6.47011 7.93147 6.59154C7.62529 6.71298 7.348 6.89729 7.11749 7.13257C6.88615 7.36562 6.70494 7.64354 6.58498 7.94922C6.46503 8.2549 6.40888 8.58189 6.41999 8.91007V9.33757C6.4229 9.45111 6.40167 9.56397 6.35771 9.6687C6.31375 9.77342 6.24806 9.86762 6.16499 9.94507L5.84999 10.2376C5.61044 10.4621 5.41949 10.7333 5.28895 11.0345C5.15842 11.3357 5.09106 11.6605 5.09106 11.9888C5.09106 12.3171 5.15842 12.6419 5.28895 12.9431C5.41949 13.2444 5.61044 13.5156 5.84999 13.7401L6.16499 14.0326C6.24806 14.11 6.31375 14.2042 6.35771 14.3089C6.40167 14.4137 6.4229 14.5265 6.41999 14.6401V15.0676C6.40888 15.3958 6.46503 15.7227 6.58498 16.0284C6.70494 16.3341 6.88615 16.612 7.11749 16.8451C7.34743 17.0811 7.62463 17.2659 7.93094 17.3874C8.23725 17.5089 8.56577 17.5643 8.89499 17.5501H9.32249C9.4359 17.5481 9.54848 17.5698 9.65307 17.6137C9.75765 17.6576 9.85196 17.7227 9.92999 17.8051L10.2225 18.1201C10.4471 18.3608 10.7187 18.5528 11.0207 18.684C11.3226 18.8153 11.6483 18.883 11.9775 18.883C12.3067 18.883 12.6324 18.8153 12.9343 18.684C13.2362 18.5528 13.5079 18.3608 13.7325 18.1201L14.025 17.8051C14.1027 17.7224 14.197 17.6569 14.3016 17.613C14.4063 17.5691 14.519 17.5476 14.6325 17.5501H15.06C15.3892 17.5639 15.7175 17.5082 16.0238 17.3868C16.33 17.2653 16.6073 17.0807 16.8375 16.8451C17.0702 16.6127 17.2528 16.335 17.374 16.0293C17.4953 15.7236 17.5526 15.3963 17.5425 15.0676V14.6401C17.5385 14.5253 17.5592 14.411 17.6032 14.3049C17.6472 14.1988 17.7135 14.1034 17.7975 14.0251L18.12 13.7326ZM17.025 12.5626L16.7175 12.8551C16.4687 13.0902 16.273 13.3757 16.1437 13.6927C16.0143 14.0096 15.9542 14.3505 15.9675 14.6926V15.1201C15.9712 15.2295 15.9525 15.3385 15.9125 15.4404C15.8725 15.5423 15.8121 15.6349 15.735 15.7126C15.5752 15.8641 15.3627 15.9475 15.1425 15.9451H14.715C14.3731 15.9328 14.0324 15.9933 13.7156 16.1226C13.3989 16.2519 13.1132 16.447 12.8775 16.6951L12.585 17.0101C12.4292 17.164 12.219 17.2503 12 17.2503C11.781 17.2503 11.5708 17.164 11.415 17.0101L11.1225 16.6951C10.8979 16.4572 10.627 16.268 10.3265 16.139C10.0259 16.01 9.70207 15.944 9.37499 15.9451H8.86499C8.75581 15.9482 8.64714 15.929 8.54564 15.8886C8.44414 15.8482 8.35195 15.7876 8.27471 15.7104C8.19748 15.6331 8.13682 15.5409 8.09646 15.4394C8.0561 15.3379 8.03688 15.2293 8.03999 15.1201V14.6926C8.05325 14.3505 7.99318 14.0096 7.86383 13.6927C7.73447 13.3757 7.53881 13.0902 7.28999 12.8551L6.97499 12.5626C6.89507 12.4875 6.83138 12.3969 6.78784 12.2963C6.7443 12.1957 6.72184 12.0872 6.72184 11.9776C6.72184 11.8679 6.7443 11.7595 6.78784 11.6589C6.83138 11.5582 6.89507 11.4676 6.97499 11.3926L7.28249 11.1001C7.53131 10.865 7.72697 10.5794 7.85633 10.2625C7.98568 9.94556 8.04575 9.60463 8.03249 9.26257V8.83507C8.02826 8.72803 8.0453 8.6212 8.08263 8.52078C8.11997 8.42037 8.17685 8.32836 8.24999 8.25007C8.40935 8.09785 8.62214 8.01435 8.84249 8.01757H9.26999C9.6111 8.03222 9.95133 7.97276 10.2672 7.84328C10.5832 7.71381 10.8673 7.51739 11.1 7.26757L11.3925 6.95257C11.4692 6.87547 11.5603 6.81429 11.6607 6.77254C11.7611 6.73079 11.8688 6.7093 11.9775 6.7093C12.0862 6.7093 12.1939 6.73079 12.2943 6.77254C12.3947 6.81429 12.4858 6.87547 12.5625 6.95257L12.855 7.26007C13.0904 7.50851 13.376 7.7039 13.6929 7.83323C14.0097 7.96256 14.3505 8.02285 14.6925 8.01007H15.12C15.2298 8.00691 15.3391 8.02635 15.4412 8.06716C15.5432 8.10798 15.6357 8.16931 15.7131 8.24736C15.7904 8.32541 15.8509 8.41851 15.8908 8.5209C15.9307 8.62328 15.9491 8.73277 15.945 8.84257V9.27007C15.9317 9.61213 15.9918 9.95306 16.1212 10.27C16.2505 10.5869 16.4462 10.8725 16.695 11.1076L17.01 11.4001C17.0899 11.4751 17.1536 11.5657 17.1971 11.6664C17.2407 11.767 17.2631 11.8754 17.2631 11.9851C17.2631 12.0947 17.2407 12.2032 17.1971 12.3038C17.1536 12.4044 17.0899 12.495 17.01 12.5701L17.025 12.5626Z"
                      fill="#C6D92C"
                    />
                    <path
                      d="M3.06 12.4203C3.14095 12.2939 3.18987 12.1498 3.2025 12.0003C3.19955 9.99076 3.88464 8.0408 5.14382 6.47472C6.403 4.90864 8.16038 3.82084 10.1237 3.39225C12.0869 2.96366 14.1378 3.22011 15.9351 4.11895C17.7323 5.01779 19.1677 6.50484 20.0025 8.33276C20.0411 8.43605 20.1006 8.53027 20.1773 8.60954C20.2539 8.68882 20.3461 8.75144 20.448 8.79351C20.5499 8.83558 20.6595 8.85619 20.7697 8.85406C20.8799 8.85193 20.9886 8.82709 21.0888 8.78111C21.189 8.73513 21.2787 8.66899 21.3522 8.58681C21.4258 8.50464 21.4816 8.40819 21.5162 8.30348C21.5508 8.19878 21.5634 8.08807 21.5533 7.97826C21.5432 7.86846 21.5106 7.76191 21.4575 7.66527C20.5511 5.68296 19.0446 4.03572 17.1508 2.95647C15.2571 1.87722 13.072 1.42056 10.9045 1.65108C8.73706 1.88159 6.69689 2.7876 5.0725 4.24101C3.4481 5.69441 2.32168 7.62167 1.8525 9.75027L1.5 9.15026C1.38066 8.96925 1.19429 8.84307 0.981906 8.79947C0.769521 8.75586 0.548514 8.79842 0.367503 8.91777C0.186493 9.03711 0.060307 9.22348 0.0167048 9.43586C-0.0268974 9.64825 0.0156558 9.86925 0.135003 10.0503L1.7325 12.4278C1.79681 12.5483 1.89284 12.6491 2.01022 12.719C2.12759 12.789 2.26186 12.8256 2.39852 12.8248C2.53517 12.8241 2.66902 12.786 2.7856 12.7147C2.90218 12.6434 2.99707 12.5416 3.06 12.4203Z"
                      fill="white"
                    />
                    <path
                      d="M23.865 13.958L22.2675 11.558C22.1743 11.4063 22.0338 11.2895 21.8676 11.2257C21.7014 11.1619 21.5188 11.1546 21.3481 11.205C21.1774 11.2553 21.028 11.3606 20.923 11.5043C20.8181 11.6481 20.7634 11.8225 20.7675 12.0005C20.7705 14.01 20.0854 15.9599 18.8262 17.526C17.567 19.0921 15.8096 20.1799 13.8463 20.6085C11.8831 21.0371 9.83224 20.7806 8.03496 19.8818C6.23767 18.9829 4.80227 17.4959 3.9675 15.668C3.92888 15.5647 3.86939 15.4705 3.79274 15.3912C3.71609 15.3119 3.62392 15.2493 3.52199 15.2072C3.42006 15.1651 3.31056 15.1445 3.20031 15.1467C3.09006 15.1488 2.98143 15.1736 2.88121 15.2196C2.78098 15.2656 2.6913 15.3317 2.61778 15.4139C2.54425 15.4961 2.48845 15.5925 2.45385 15.6972C2.41925 15.8019 2.40661 15.9127 2.4167 16.0225C2.42679 16.1323 2.4594 16.2388 2.5125 16.3355C3.41285 18.3294 4.91934 19.9884 6.81746 21.0763C8.71558 22.1642 10.9086 22.6254 13.0841 22.3944C15.2596 22.1634 17.3068 21.2519 18.9342 19.7896C20.5615 18.3274 21.686 16.389 22.1475 14.2505L22.5375 14.8355C22.6644 14.987 22.8428 15.0864 23.0385 15.1146C23.2342 15.1427 23.4334 15.0976 23.5979 14.988C23.7624 14.8783 23.8807 14.7118 23.9299 14.5203C23.9792 14.3288 23.9561 14.1259 23.865 13.9505V13.958Z"
                      fill="white"
                    />
                  </svg>
                  Set Schedule Repeater
                </button>

                {/* 5. Filter by Week/Month */}
                <div className="w-[180px]">
                  <select
                    className={`w-full h-10 px-4 rounded-lg text-sm outline-none border ${
                      theme === "dark"
                        ? "bg-[#232323] border-[#383838] text-gray-300 focus:border-[#B6CB24]"
                        : "bg-gray-50 border-gray-300 text-gray-800"
                    }`}
                    value={
                      calendarView === "month"
                        ? "Filter by Month"
                        : "Filter by Week"
                    }
                    onChange={handleFilterChange}
                  >
                    <option>Filter by Week</option>
                    <option>Filter by Month</option>
                  </select>
                </div>
              </div>
              {/* Calendar with overlay message - only for blocking states */}
              <div className="relative">
                {renderCalendar()}
                {/* Overlay message ONLY when no gym selected, loading, or error - NOT for empty schedules */}
                {(!selectedGymId ||
                  isSessionSchedulesLoading ||
                  isSessionSchedulesError) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-lg">
                    <div className="text-center">
                      {!selectedGymId ? (
                        <>
                          <svg
                            className="mx-auto mb-4"
                            width="64"
                            height="64"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                              fill="#6B7280"
                            />
                          </svg>
                          <p className="text-lg font-medium mb-2 text-gray-300">
                            No Gym Selected
                          </p>
                          <p className="text-sm text-gray-400">
                            Please select a gym from the dropdown above to view
                            schedules
                          </p>
                        </>
                      ) : isSessionSchedulesLoading ? (
                        <>
                          <div
                            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent mb-4"
                            style={{ color: "#B6CB24" }}
                            role="status"
                          >
                            <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                              Loading...
                            </span>
                          </div>
                          <p className="text-sm text-gray-300">
                            Loading schedules...
                          </p>
                        </>
                      ) : (
                        <>
                          <svg
                            className="mx-auto mb-4"
                            width="64"
                            height="64"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                              fill="#EF4444"
                            />
                          </svg>
                          <p className="text-lg font-medium mb-2 text-red-400">
                            Failed to Load Schedules
                          </p>
                          <p className="text-sm text-gray-400">
                            There was an error loading the schedule data
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {deleteTarget && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
            theme === "dark" ? "bg-black/60" : "bg-black/40"
          }`}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-xl border ${
              theme === "dark"
                ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
                : "bg-white text-gray-900 border-gray-200"
            }`}
          >
            <button
              onClick={closeDelete}
              aria-label="Close"
              className={`absolute top-3 right-3 p-2 rounded-full ${
                theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
              }`}
              disabled={isDeleting}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>

            <div className="px-6 py-6 text-center">
              <div
                className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${
                  theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                }`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#B6CB24">
                  <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">
                Delete Scheduled Session
              </h3>
              <p
                className={`${
                  theme === "dark" ? "text-gray-300" : "text-gray-600"
                } mb-6`}
              >
                Are you sure you want to delete session "
                {deleteTarget.sessionItem?.name}"?
              </p>

              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid #D92C2C",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Confirm and delete"}
                </button>
                <button
                  type="button"
                  onClick={closeDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-black rounded-[10px] border"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  May be later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Repeater Modal */}
      {isRepeaterModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div
            className={`w-[480px] rounded-2xl p-6 shadow-2xl border ${
              theme === "dark"
                ? "bg-[#1C1C1E] border-[#2C2C2E] text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1 flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                    theme === "dark" ? "bg-[#2C2C2E]" : "bg-gray-100"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M11.9998 8.77539C11.3674 8.77541 10.7491 8.96268 10.2229 9.31359C9.69678 9.6645 9.28631 10.1633 9.04328 10.7472C8.80024 11.3311 8.73552 11.9738 8.85727 12.5944C8.97903 13.215 9.28181 13.7857 9.72744 14.2345C10.1731 14.6832 10.7416 14.99 11.3613 15.1161C11.9811 15.2422 12.6243 15.182 13.2098 14.943C13.7954 14.7041 14.2971 14.2971 14.6517 13.7734C15.0062 13.2498 15.1978 12.6328 15.2023 12.0004C15.2053 11.5779 15.1246 11.1591 14.965 10.7679C14.8054 10.3768 14.57 10.0211 14.2723 9.72132C13.9746 9.42156 13.6206 9.18365 13.2305 9.0213C12.8405 8.85896 12.4222 8.77538 11.9998 8.77539ZM11.9998 13.5754C11.6829 13.5754 11.3731 13.4811 11.11 13.3045C10.8468 13.128 10.6421 12.8771 10.5219 12.5839C10.4016 12.2907 10.3713 11.9683 10.4348 11.6579C10.4983 11.3474 10.6527 11.0628 10.8784 10.8403C11.1041 10.6178 11.3908 10.4675 11.7022 10.4084C12.0135 10.3493 12.3354 10.3842 12.6269 10.5085C12.9184 10.6329 13.1663 10.8412 13.3391 11.1068C13.5119 11.3725 13.6017 11.6835 13.5973 12.0004C13.5895 12.4195 13.4179 12.819 13.1194 13.1133C12.8209 13.4077 12.419 13.5735 11.9998 13.5754Z"
                      fill="white"
                    />
                    <path
                      d="M18.1202 13.7326C18.3598 13.5081 18.5507 13.2369 18.6813 12.9356C18.8118 12.6344 18.8792 12.3096 18.8792 11.9813C18.8792 11.653 18.8118 11.3282 18.6813 11.027C18.5507 10.7258 18.3598 10.4546 18.1202 10.2301L17.8052 9.93757C17.7222 9.86012 17.6565 9.76592 17.6125 9.6612C17.5686 9.55648 17.5473 9.44361 17.5502 9.33007V8.90257C17.5599 8.57391 17.5024 8.24672 17.3812 7.94107C17.2599 7.63543 17.0776 7.35775 16.8452 7.12507C16.6138 6.89152 16.3362 6.70885 16.0301 6.58875C15.724 6.46864 15.3962 6.41375 15.0677 6.42757H14.6402C14.5259 6.42836 14.4125 6.40619 14.3069 6.36239C14.2013 6.31859 14.1055 6.25405 14.0252 6.17257L13.7327 5.85757C13.2672 5.39226 12.6359 5.13086 11.9777 5.13086C11.3195 5.13086 10.6883 5.39226 10.2227 5.85757L9.93023 6.17257C9.85221 6.2549 9.7579 6.32008 9.65331 6.36398C9.54872 6.40788 9.43615 6.42954 9.32274 6.42757H8.89523C8.56613 6.41425 8.23788 6.47011 7.93171 6.59154C7.62554 6.71298 7.34824 6.89729 7.11773 7.13257C6.8864 7.36562 6.70518 7.64354 6.58523 7.94922C6.46528 8.2549 6.40913 8.58189 6.42024 8.91007V9.33757C6.42314 9.45111 6.40191 9.56397 6.35795 9.6687C6.314 9.77342 6.24831 9.86762 6.16524 9.94507L5.85024 10.2376C5.61069 10.4621 5.41974 10.7333 5.2892 11.0345C5.15866 11.3357 5.09131 11.6605 5.09131 11.9888C5.09131 12.3171 5.15866 12.6419 5.2892 12.9431C5.41974 13.2444 5.61069 13.5156 5.85024 13.7401L6.16524 14.0326C6.24831 14.11 6.314 14.2042 6.35795 14.3089C6.40191 14.4137 6.42314 14.5265 6.42024 14.6401V15.0676C6.40913 15.3958 6.46528 15.7227 6.58523 16.0284C6.70518 16.3341 6.8864 16.612 7.11773 16.8451C7.34767 17.0811 7.62487 17.2659 7.93118 17.3874C8.2375 17.5089 8.56602 17.5643 8.89523 17.5501H9.32274C9.43615 17.5481 9.54872 17.5698 9.65331 17.6137C9.7579 17.6576 9.85221 17.7227 9.93023 17.8051L10.2227 18.1201C10.4473 18.3608 10.719 18.5528 11.0209 18.684C11.3228 18.8153 11.6485 18.883 11.9777 18.883C12.307 18.883 12.6326 18.8153 12.9346 18.684C13.2365 18.5528 13.5082 18.3608 13.7327 18.1201L14.0252 17.8051C14.103 17.7224 14.1972 17.6569 14.3019 17.613C14.4065 17.5691 14.5193 17.5476 14.6327 17.5501H15.0602C15.3894 17.5639 15.7178 17.5082 16.024 17.3868C16.3303 17.2653 16.6075 17.0807 16.8377 16.8451C17.0704 16.6127 17.253 16.335 17.3743 16.0293C17.4955 15.7236 17.5529 15.3963 17.5427 15.0676V14.6401C17.5388 14.5253 17.5595 14.411 17.6034 14.3049C17.6474 14.1988 17.7137 14.1034 17.7977 14.0251L18.1202 13.7326ZM17.0252 12.5626L16.7177 12.8551C16.4689 13.0902 16.2733 13.3757 16.1439 13.6927C16.0145 14.0096 15.9545 14.3505 15.9677 14.6926V15.1201C15.9714 15.2295 15.9527 15.3385 15.9127 15.4404C15.8728 15.5423 15.8123 15.6349 15.7352 15.7126C15.5754 15.8641 15.3629 15.9475 15.1427 15.9451H14.7152C14.3733 15.9328 14.0327 15.9933 13.7159 16.1226C13.3991 16.2519 13.1134 16.447 12.8777 16.6951L12.5852 17.0101C12.4294 17.164 12.2192 17.2503 12.0002 17.2503C11.7812 17.2503 11.5711 17.164 11.4152 17.0101L11.1227 16.6951C10.8982 16.4572 10.6273 16.268 10.3267 16.139C10.0261 16.01 9.70231 15.944 9.37524 15.9451H8.86523C8.75605 15.9482 8.64738 15.929 8.54588 15.8886C8.44438 15.8482 8.35219 15.7876 8.27496 15.7104C8.19772 15.6331 8.13706 15.5409 8.0967 15.4394C8.05634 15.3379 8.03713 15.2293 8.04024 15.1201V14.6926C8.05349 14.3505 7.99343 14.0096 7.86407 13.6927C7.73471 13.3757 7.53906 13.0902 7.29024 12.8551L6.97524 12.5626C6.89532 12.4875 6.83162 12.3969 6.78808 12.2963C6.74454 12.1957 6.72208 12.0872 6.72208 11.9776C6.72208 11.8679 6.74454 11.7595 6.78808 11.6589C6.83162 11.5582 6.89532 11.4676 6.97524 11.3926L7.28274 11.1001C7.53156 10.865 7.72721 10.5794 7.85657 10.2625C7.98593 9.94556 8.04599 9.60463 8.03274 9.26257V8.83507C8.0285 8.72803 8.04554 8.6212 8.08288 8.52078C8.12021 8.42037 8.1771 8.32836 8.25024 8.25007C8.40959 8.09785 8.62238 8.01435 8.84274 8.01757H9.27024C9.61134 8.03222 9.95157 7.97276 10.2675 7.84328C10.5834 7.71381 10.8675 7.51739 11.1002 7.26757L11.3927 6.95257C11.4694 6.87547 11.5606 6.81429 11.661 6.77254C11.7613 6.73079 11.869 6.7093 11.9777 6.7093C12.0865 6.7093 12.1941 6.73079 12.2945 6.77254C12.3949 6.81429 12.4861 6.87547 12.5627 6.95257L12.8552 7.26007C13.0906 7.50851 13.3763 7.7039 13.6931 7.83323C14.01 7.96256 14.3507 8.02285 14.6927 8.01007H15.1202C15.2301 8.00691 15.3394 8.02635 15.4414 8.06716C15.5434 8.10798 15.636 8.16931 15.7133 8.24736C15.7907 8.32541 15.8512 8.41851 15.8911 8.5209C15.9309 8.62328 15.9494 8.73277 15.9452 8.84257V9.27007C15.932 9.61213 15.992 9.95306 16.1214 10.27C16.2508 10.5869 16.4464 10.8725 16.6952 11.1076L17.0102 11.4001C17.0902 11.4751 17.1538 11.5657 17.1974 11.6664C17.2409 11.767 17.2634 11.8754 17.2634 11.9851C17.2634 12.0947 17.2409 12.2032 17.1974 12.3038C17.1538 12.4044 17.0902 12.495 17.0102 12.5701L17.0252 12.5626Z"
                      fill="#C6D92C"
                    />
                    <path
                      d="M3.06 12.4193C3.14095 12.293 3.18987 12.1488 3.2025 11.9993C3.19955 9.98978 3.88464 8.03982 5.14382 6.47374C6.403 4.90766 8.16038 3.81987 10.1237 3.39128C12.0869 2.96269 14.1378 3.21914 15.9351 4.11798C17.7323 5.01682 19.1677 6.50387 20.0025 8.33179C20.0411 8.43507 20.1006 8.52929 20.1773 8.60857C20.2539 8.68784 20.3461 8.75046 20.448 8.79253C20.5499 8.8346 20.6595 8.85521 20.7697 8.85308C20.8799 8.85095 20.9886 8.82612 21.0888 8.78014C21.189 8.73416 21.2787 8.66801 21.3522 8.58584C21.4258 8.50366 21.4816 8.40721 21.5162 8.30251C21.5508 8.1978 21.5634 8.0871 21.5533 7.97729C21.5432 7.86748 21.5106 7.76093 21.4575 7.66429C20.5511 5.68198 19.0446 4.03475 17.1508 2.9555C15.2571 1.87624 13.072 1.41959 10.9045 1.6501C8.73706 1.88061 6.69689 2.78662 5.0725 4.24003C3.4481 5.69344 2.32168 7.62069 1.8525 9.74929L1.5 9.14929C1.38066 8.96828 1.19429 8.84209 0.981906 8.79849C0.769521 8.75489 0.548514 8.79744 0.367503 8.91679C0.186493 9.03614 0.060307 9.2225 0.0167048 9.43489C-0.0268974 9.64727 0.0156558 9.86828 0.135003 10.0493L1.7325 12.4268C1.79681 12.5474 1.89284 12.6481 2.01022 12.7181C2.12759 12.7881 2.26186 12.8246 2.39852 12.8239C2.53517 12.8231 2.66902 12.785 2.7856 12.7137C2.90218 12.6424 2.99707 12.5406 3.06 12.4193Z"
                      fill="white"
                    />
                    <path
                      d="M23.8648 13.958L22.2673 11.558C22.1741 11.4063 22.0336 11.2895 21.8674 11.2257C21.7012 11.1619 21.5186 11.1546 21.3479 11.205C21.1771 11.2553 21.0277 11.3606 20.9228 11.5043C20.8178 11.6481 20.7632 11.8225 20.7673 12.0005C20.7702 14.01 20.0851 15.9599 18.8259 17.526C17.5668 19.0921 15.8094 20.1799 13.8461 20.6085C11.8828 21.0371 9.832 20.7806 8.03471 19.8818C6.23743 18.9829 4.80203 17.4959 3.96726 15.668C3.92864 15.5647 3.86915 15.4705 3.7925 15.3912C3.71585 15.3119 3.62368 15.2493 3.52175 15.2072C3.41982 15.1651 3.31031 15.1445 3.20006 15.1467C3.08982 15.1488 2.98119 15.1736 2.88096 15.2196C2.78074 15.2656 2.69106 15.3317 2.61753 15.4139C2.544 15.4961 2.4882 15.5925 2.4536 15.6972C2.41901 15.8019 2.40636 15.9127 2.41645 16.0225C2.42655 16.1323 2.45916 16.2388 2.51226 16.3355C3.41261 18.3294 4.9191 19.9884 6.81722 21.0763C8.71534 22.1642 10.9083 22.6254 13.0838 22.3944C15.2594 22.1634 17.3066 21.2519 18.9339 19.7896C20.5612 18.3274 21.6857 16.389 22.1473 14.2505L22.5373 14.8355C22.6642 14.987 22.8426 15.0864 23.0383 15.1146C23.234 15.1427 23.4332 15.0976 23.5977 14.988C23.7622 14.8783 23.8804 14.7118 23.9297 14.5203C23.979 14.3288 23.9559 14.1259 23.8648 13.9505V13.958Z"
                      fill="white"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-[#B6CB24]">
                  Set Schedule Repeater
                </h3>
              </div>
              <button
                onClick={() => setIsRepeaterModalOpen(false)}
                className={`p-2 rounded-full hover:bg-white/10 transition ${
                  theme === "dark" ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="mb-8">
              <label
                className={`block text-sm font-medium mb-2 ${
                  theme === "dark" ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Choose Time Period
              </label>
              <div className="relative">
                <select
                  className={`w-full h-12 px-4 rounded-xl text-sm outline-none border appearance-none ${
                    theme === "dark"
                      ? "bg-[#151515] border-[#383838] text-white focus:border-[#B6CB24]"
                      : "bg-gray-50 border-gray-300 text-gray-900 focus:border-[#B6CB24]"
                  }`}
                  value={repeatOption}
                  onChange={(e) => setRepeatOption(e.target.value as any)}
                >
                  <option value="No Repeat">No Repeat</option>
                  {Object.values(GymRepeatSchedulePeriodType).map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={
                      theme === "dark" ? "text-gray-400" : "text-gray-500"
                    }
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  if (!selectedGymId) {
                    toast.error("Please select a gym first.");
                    return;
                  }
                  try {
                    const isNoRepeat = repeatOption === "No Repeat";
                    await editGym({
                      gymid: selectedGymId,
                      data: {
                        autoRepeatSchedule: !isNoRepeat,
                        repeatPeriodType: isNoRepeat ? undefined : repeatOption,
                      },
                    }).unwrap();
                    toast.success("Schedule repeater updated successfully!");
                    setIsRepeaterModalOpen(false);
                  } catch (error) {
                    toast.error("Failed to update schedule repeater.");
                  }
                }}
                className="flex-1 h-11 rounded-xl font-medium text-black transition hover:opacity-90"
                style={{
                  background:
                    "linear-gradient(180deg, #B6CB24 0%, #8E9E13 100%)",
                  border: "1px solid #B6CB24",
                  boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                }}
              >
                Apply
              </button>
              <button
                onClick={() => setIsRepeaterModalOpen(false)}
                className={`flex-1 h-11 rounded-xl font-medium border transition ${
                  theme === "dark"
                    ? "border-[#383838] hover:bg-[#2C2C2E] text-white"
                    : "border-gray-300 hover:bg-gray-50 text-gray-700"
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </DndProvider>
  );
};

export default Schedule;
